import React from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ShoppingCart, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { motion } from 'framer-motion';

// Helper to get label position classes
const getLabelPositionClasses = (position) => {
  switch (position) {
    case 'top-right':
      return 'top-2 right-2';
    case 'bottom-left':
      return 'bottom-2 left-2';
    case 'bottom-right':
      return 'bottom-2 right-2';
    case 'top-left':
    default:
      return 'top-2 left-2';
  }
};

export const ProductCard = ({ product }) => {
  const { addToCart, formatPrice, getLocalizedText, t } = useApp();

  const hasDiscount = product.discount_percent > 0;
  const originalPrice = formatPrice(product.prices);
  const discountedPrice = hasDiscount
    ? formatPrice({
        ron: product.prices.ron * (1 - product.discount_percent / 100),
        eur: product.prices.eur * (1 - product.discount_percent / 100),
      })
    : null;

  // Handle image loading - use placeholder if WordPress image fails
  const imageUrl = product.images?.[0] || 'https://images.unsplash.com/photo-1763478959550-13d564c5a62d?q=80&w=800&auto=format&fit=crop';

  // Get active labels
  const activeLabels = (product.labels || []).filter(label => label.is_active !== false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="group product-card"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`} className="block">
        <div className="relative aspect-square overflow-hidden rounded-lg bg-gray-100 mb-3">
          <img
            src={imageUrl}
            alt={getLocalizedText(product.name)}
            className="product-image w-full h-full object-cover"
            loading="lazy"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1763478959550-13d564c5a62d?q=80&w=800&auto=format&fit=crop';
            }}
          />
          {/* Discount badge - top-left */}
          {hasDiscount && (
            <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded z-10">
              -{product.discount_percent}%
            </span>
          )}
          {/* Featured badge - only if no discount */}
          {product.is_featured && !hasDiscount && activeLabels.length === 0 && (
            <span className="absolute top-2 left-2 bg-gold text-white text-xs font-bold px-2 py-1 rounded z-10">
              Featured
            </span>
          )}
          {/* SKU badge - top-right corner */}
          {product.sku && (
            <span 
              className="absolute top-2 right-2 bg-gray-800/80 text-white text-xs font-mono px-2 py-1 rounded z-10"
              data-testid="product-sku"
            >
              {product.sku}
            </span>
          )}
          {/* Custom product labels */}
          {activeLabels.map((label, index) => {
            // Skip top-left if discount is present, skip top-right if SKU is present
            const position = label.position || 'top-left';
            if (hasDiscount && position === 'top-left') return null;
            if (product.sku && position === 'top-right') return null;
            
            return (
              <div
                key={index}
                className={`absolute ${getLabelPositionClasses(position)} z-10`}
                data-testid={`product-label-${index}`}
              >
                {label.image_url ? (
                  <img
                    src={label.image_url}
                    alt=""
                    className="h-8 w-auto object-contain"
                  />
                ) : label.text ? (
                  <span className="bg-primary text-white text-xs font-bold px-2 py-1 rounded shadow-sm">
                    {getLocalizedText(label.text)}
                  </span>
                ) : null}
              </div>
            );
          })}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
          <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-300">
            <Button
              onClick={(e) => {
                e.preventDefault();
                addToCart(product);
              }}
              size="sm"
              className="w-full bg-white text-foreground hover:bg-primary hover:text-white transition-colors text-sm"
              data-testid={`add-to-cart-${product.id}`}
            >
              <ShoppingCart className="mr-1 h-3 w-3" />
              {t('addToCart')}
            </Button>
          </div>
        </div>
      </Link>
      <div className="space-y-1">
        <Link to={`/product/${product.id}`}>
          <h3 className="text-sm font-medium text-foreground hover:text-primary transition-colors line-clamp-2 leading-tight">
            {getLocalizedText(product.name)}
          </h3>
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          {hasDiscount ? (
            <>
              <span className="text-base font-semibold text-primary">{discountedPrice}</span>
              <span className="text-xs text-muted-foreground line-through">{originalPrice}</span>
            </>
          ) : (
            <span className="text-base font-semibold text-foreground">{originalPrice}</span>
          )}
        </div>
      </div>
    </motion.div>
  );
};
